import { CastApiErrorCode } from '../types/errors'
import CastAPI, { CastParamsOptional, CastParamsSaveCast } from './CastAPI'
import { Cast } from './cast'
import { CollectionNames } from '@/modules/database/utils/collectionNames'
import firebase from 'firebase/app'
import { CastFanRoom } from './CastFanRoom'
import Convertable from '@/classes/Convertable'
import { generateRandomString } from '@/modules/common/utils'
import { CastStatus, CastType } from '@/types/casts'
import { Result } from '@/modules/errorHandling/results'
import { SceneSourceType } from '@/modules/scenes/types'
import ActiveState from './ActiveState'

export default class FanRoomCastAPI extends CastAPI {

  private _castDocRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>|null = null

  override async createCast (castParams: CastParamsSaveCast<CastFanRoom>, _currentUserId: string,
    optionalParams?: CastParamsOptional): Promise<Result<Cast, CastApiErrorCode>>
  {
    this._teamId = castParams.team_id
    const version = await this.getDefaultVersion()
    try {
      this._castDocRef = this._db.collection(CollectionNames.CASTS).doc()
      const result = await this._db.runTransaction(async (transaction: firebase.firestore.Transaction) => {
        this._transaction = transaction
        if (this._castDocRef === null) {
          return Result.fail(CastApiErrorCode.CREATE_CAST, 'Could not create cast document')
        }
        const castId = this._castDocRef.id
        const eventId = this.createBlankEvent({ ...castParams, castId, castType: CastType.FanRoom }, optionalParams)
        const cast = this.createCastObj(version, eventId, castId, castParams)
        cast.token = generateRandomString(24)
        // @ts-ignore @tag does not exist on a Cast class but we want to add it anyway in the DB.
        if (optionalParams?.tag) cast['@tag'] = optionalParams?.tag
        this.createPointerData(castId, castParams.name, cast.token, version, optionalParams)
        this.saveCast(cast)
        if (optionalParams?.skipTeamLastEventTimestamp !== true) this.setLastEventTimestamp()
        return Result.success(cast)
      })
      if (result.isSuccess) return result.convert()
      else return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
    } catch (error) {
      console.error(`Error creating cast ${ error }`)
    }
    return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
  }

  async updateCast (castParams: Cast & { id: string }):
      Promise<Result<Cast, CastApiErrorCode>> {
    this._teamId = castParams.team_id ?? null
    try {
      const cast = await this.updateExistingCast(castParams)
      if (cast.isSuccess) {
        return Result.success(cast.value as CastFanRoom)
      }
    } catch (error) {
      console.error('Error updating cast', error)
    }
    return Result.fail(CastApiErrorCode.UPDATE_CAST, 'Error updating cast')
  }

  private saveCast (cast: Cast) {
    if (this._castDocRef === null) {
      console.error('Cannot save cast, need to set _castDocRef first')
      return
    }
    if (this._transaction === null) {
      console.error('Cannot save cast, transaction is not created yet')
      return
    }
    const chatDocRef = this._db.collection(CollectionNames.CHATS).doc()
    cast.chat_id = chatDocRef.id
    const chatData: { [k: string]: any } = { team_id: cast.team_id, messages: [] }
    // @ts-ignore @tag does not exist on a Cast class but we want to add it anyway in the DB.
    if (cast['@tag']) chatData['@tag'] = cast['@tag']
    this._transaction.set(chatDocRef, chatData)
    this._transaction.set(this._castDocRef, Convertable.toObject({ ...cast }))
  }

  private createCastObj (version: string, eventId: string, castId: string,
                         castParams: CastParamsSaveCast<CastFanRoom>): CastFanRoom {
    return new CastFanRoom({
      ...castParams,
      version,
      event_id: eventId,
      id: castId,
      create_event: false,
      status: CastStatus.Booting
    })
  }

  private async cleanAnonymousSources (cast: Cast) {
    for (const scene of Object.values(cast.scenes)) {
      if (cast.getSceneSourceType(scene.id) === SceneSourceType.TalentAnonymous) {
        const anonBoxId = cast.getAnonymousBoxId(scene.id)
        if (!anonBoxId) continue
        const content = cast.findContentWithBox(scene.id, anonBoxId)
        if (!content || !content.id || !content.user) continue
        cast.replaceCasterContent({
          sceneId: scene.id, boxId: anonBoxId, oldContentId: content.id, casterId: anonBoxId
        })
      }
    }
  }

  public override async getCopyOfCast (castId: string): Promise<Result<Cast, CastApiErrorCode>> {
    const result = await super.getCopyOfCast(castId)
    if (result.isFailure) return result
    const castCopy = result.value
    if (castCopy.castType === CastType.FanRoom) castCopy.activeState = new ActiveState()
    await this.cleanAnonymousSources(castCopy)
    return Result.success(castCopy)
  }
}
